import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";

import {
  Box,
  Paper,
  Typography,
  FormControl,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  Button,
  Snackbar,
  Autocomplete,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Tooltip,
  Divider,
} from "@mui/material";

import {
  decryptText,
  decryptAppId,
  encryptAppId,
} from "../../../utils/encryption";

import LaboratoryService from "../../../services/laboratoryService";

const AddScopeBasedOnProduct = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { orgId: urlOrgId } = useParams();

  const getQueryParam = useCallback(
    (paramName) => {
      const searchParams = new URLSearchParams(location.search);
      return searchParams.get(paramName);
    },
    [location.search]
  );

  const getOrgId = useCallback(() => {
    const encryptedOrgId = getQueryParam("orgId");

    if (encryptedOrgId) {
      try {
        return decryptText(encryptedOrgId);
      } catch (error) {
        console.error("Error decrypting orgId:", error);
        return encryptedOrgId;
      }
    }

    return urlOrgId || null;
  }, [getQueryParam, urlOrgId]);

  const getAppId = useCallback(() => {
    const encryptedAppId = getQueryParam("appId");

    if (encryptedAppId) {
      try {
        return decryptAppId(encryptedAppId);
      } catch (error) {
        console.error("Error decrypting appId:", error);
        return encryptedAppId;
      }
    }

    return null;
  }, [getQueryParam]);

  const getLabTypeId = useCallback(() => {
    const encryptedLabTypeId = getQueryParam("labTypeId");

    if (!encryptedLabTypeId) return null;

    try {
      return decryptText(encryptedLabTypeId);
    } catch (error) {
      console.error("Error decrypting labTypeId:", error);
      return null;
    }
  }, [getQueryParam]);

  const [orgId] = useState(getOrgId());
  const [appId] = useState(getAppId());
  const [labTypeId] = useState(getLabTypeId());
  const [materials, setMaterials] = useState([]);
  const [selectedMaterial, setSelectedMaterial] = useState(null);
  const [materialLoading, setMaterialLoading] = useState(false);
  const [materialError, setMaterialError] = useState("");
  const [disciplines, setDisciplines] = useState([]);
  const [disciplineLoading, setDisciplineLoading] = useState(false);
  const [disciplineError, setDisciplineError] = useState("");
  const [scopeDialogOpen, setScopeDialogOpen] = useState(false);
  const [selectedScopeDialogDiscipline,setSelectedScopeDialogDiscipline] = useState(null);
  const [scopeDialogItems, setScopeDialogItems] = useState([]);

  const [selectedScopeDialogKeys, setSelectedScopeDialogKeys] = useState([]);
  const [addingDisciplines, setAddingDisciplines] = useState(false);

  const [notification, setNotification] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const showNotification = (message, severity = "success") => {
    setNotification({
      open: true,
      message,
      severity,
    });
  };

  const closeNotification = () => {
    setNotification({
      open: false,
      message: "",
      severity: "success",
    });
  };

  const loadMaterials = useCallback(async () => {
    try {
      setMaterialLoading(true);
      setMaterialError("");

      const result =
        await LaboratoryService.getMaterialsOrProducts(labTypeId);

      if (result.success) {
        setMaterials(
          Array.isArray(result.data) ? result.data : []
        );
      } else {
        setMaterials([]);
        setMaterialError(
          result.message ||
            "No Material or Product Tested found."
        );
      }
    } catch (error) {
      console.error("Error loading materials:", error);

      setMaterials([]);

      setMaterialError(
        "Unable to load Material or Product Tested."
      );
    } finally {
      setMaterialLoading(false);
    }
  }, [labTypeId]);

  useEffect(() => {
    if (labTypeId) {
      loadMaterials();
    }
  }, [labTypeId, loadMaterials]);

  const handleMaterialChange = (event, newValue) => {
    setSelectedMaterial(newValue);
    setDisciplines([]);
    setDisciplineError("");
    setScopeDialogOpen(false);
    setSelectedScopeDialogDiscipline(null);
    setScopeDialogItems([]);
    setSelectedScopeDialogKeys([]);

    if (!newValue) return;

    const groupTested = newValue.GroupTested;

    const loadDisciplines = async () => {
      try {
        setDisciplineLoading(true);
        const result =
          await LaboratoryService.getDisciplinesListBasedOnProduct(
            appId,
            orgId,
            labTypeId,
            groupTested
          );
        if (result.success) {
          setDisciplines(
            Array.isArray(result.data)
              ? result.data
              : []
          );
        } else {
          setDisciplineError(
            result.message ||
              "No disciplines found."
          );
        }
      } catch (error) {
        console.error(
          "Error loading disciplines:",
          error
        );

        setDisciplineError(
          "Unable to load disciplines."
        );
      } finally {
        setDisciplineLoading(false);
      }
    };
    loadDisciplines();
  };

  const parseScopeMasterData = useCallback(
    (scopeMasterData) => {
      if (!scopeMasterData) return [];
      if (Array.isArray(scopeMasterData)) {
        return scopeMasterData;
      }
      if (typeof scopeMasterData === "string") {
        try {
          const parsed = JSON.parse(scopeMasterData);
          return Array.isArray(parsed)
            ? parsed
            : [];
        } catch (error) {
          console.error(
            "Error parsing ScopeMasterData:",
            error
          );
          return [];
        }
      }
      return [];
    },
    []
  );

  const normalizeScopeDialogItems = useCallback(
    (rawScopes) => {
      const parsedScopes = parseScopeMasterData(rawScopes);
      return parsedScopes.flatMap((scopeItem) => {
        if (Array.isArray(scopeItem?.items)) {
          return scopeItem.items;
        }
        if (Array.isArray(scopeItem?.sections)) {
          return scopeItem.sections.flatMap(
            (section) => {
              if (Array.isArray(section?.items)) {
                return section.items;
              }
              return [section];
            }
          );
        }
        return [scopeItem];
      });
    },
    [parseScopeMasterData]
  );

  const getScopeSelectionKey = (scope, index) => {
    if (scope?.Id != null) {
      return `id-${scope.Id}`;
    }
    if (scope?.id != null) {
      return `id-${scope.id}`;
    }
    if (scope?.TestCode) {
      return `testcode-${scope.TestCode}`;
    }
    if (scope?.TypeOfTest) {
      return `type-${scope.TypeOfTest}-${index}`;
    }
    return `index-${index}`;
  };

  const isScopeAdded = (scope) => {
    return Number(scope?.IsScopeExists) === 1;
  };

  const getAvailableScopes = (items) => {
    return items.filter(
      (scope) => !isScopeAdded(scope)
    );
  };

  const getAddedScopes = (items) => {
    return items.filter((scope) =>
      isScopeAdded(scope)
    );
  };

  const openScopeDialog = (discipline) => {
    const parsedScopes = normalizeScopeDialogItems(discipline?.ScopeMasterData);
    setSelectedScopeDialogDiscipline(discipline);
    setScopeDialogItems(parsedScopes);
    setSelectedScopeDialogKeys([]);
    setScopeDialogOpen(true);
  };

  const closeScopeDialog = () => {
    setScopeDialogOpen(false);
    setSelectedScopeDialogDiscipline(null);
    setScopeDialogItems([]);
    setSelectedScopeDialogKeys([]);
  };

  const handleDialogScopeToggle = (scopeKey,isSelected) => {
    setSelectedScopeDialogKeys((prev) => {
      if (isSelected) {
        return prev.includes(scopeKey)
          ? prev
          : [...prev, scopeKey];
      }
      return prev.filter(
        (key) => key !== scopeKey
      );
    });
  };

  const handleDialogSelectAll = (isChecked) => {
    if (isChecked) {
      const availableScopes = getAvailableScopes(scopeDialogItems);
      const allKeys = availableScopes.map((scope, index) =>
          getScopeSelectionKey(scope, index)
      );
      setSelectedScopeDialogKeys(allKeys);
    } else {
      setSelectedScopeDialogKeys([]);
    }
  };

  const handleAddSelectedScopesFromDialog =
    async () => {
      if (!selectedScopeDialogDiscipline) {
        showNotification(
          "Please select a discipline first.",
          "warning"
        );
        return;
      }
      if (Number(selectedScopeDialogDiscipline?.IsExists) !== 1) {
        showNotification(
          "Please add this discipline to the Discipline section first.",
          "warning"
        );
        return;
      }

      if (selectedScopeDialogKeys.length === 0) {
        showNotification(
          "Please select at least one scope item.",
          "warning"
        );
        return;
      }
      const selectedScopes =
        scopeDialogItems.filter(
          (scope, index) => {
            const key =
              getScopeSelectionKey(
                scope,
                index
              );

            return (
              selectedScopeDialogKeys.includes(
                key
              ) &&
              !isScopeAdded(scope)
            );
          }
        );

      if (selectedScopes.length === 0) {
        showNotification(
          "No available scope selected to add.",
          "warning"
        );
        return;
      }
      setAddingDisciplines(true);

      try {
        const result =
          await LaboratoryService.saveSelectedScopes(
            selectedScopes,
            appId,
            orgId,
            selectedScopeDialogDiscipline.DisplineId
          );

        if (result.success) {
          showNotification(
            `Added ${selectedScopes.length} scope(s) from ${
              selectedScopeDialogDiscipline.DisciplineName ||
              "discipline"
            }.`,
            "success"
          );

          closeScopeDialog();

          // Refresh discipline list
          if (selectedMaterial) {
            const groupTested =
              selectedMaterial.GroupTested;

            try {
              setDisciplineLoading(true);

              const res =
                await LaboratoryService.getDisciplinesListBasedOnProduct(
                  appId,
                  orgId,
                  labTypeId,
                  groupTested
                );

              if (res.success) {
                setDisciplines(
                  Array.isArray(res.data)
                    ? res.data
                    : []
                );
              }
            } catch (error) {
              console.error(
                "Error refreshing disciplines:",
                error
              );
            } finally {
              setDisciplineLoading(false);
            }
          }
        } else {
          showNotification(
            result.message ||
              "Failed to add selected scopes.",
            "error"
          );
        }
      } catch (error) {
        console.error(
          "Error adding selected scopes:",
          error
        );
        showNotification(
          error.message ||
            "Error adding selected scopes.",
          "error"
        );
      } finally {
        setAddingDisciplines(false);
      }
    };

  const selectedAvailableCount =
    scopeDialogItems.filter(
      (scope, index) => {
        const key =
          getScopeSelectionKey(
            scope,
            index
          );
        return (
          selectedScopeDialogKeys.includes(
            key
          ) &&
          !isScopeAdded(scope)
        );
      }
    ).length;


  const totalScopeCount = scopeDialogItems.length;
  const addedScopeCount = getAddedScopes(scopeDialogItems).length;
  const availableScopeCount = getAvailableScopes(scopeDialogItems).length;
  const isDisciplineAdded = Number(selectedScopeDialogDiscipline?.IsExists) === 1;
  const allAvailableSelected = availableScopeCount > 0 && selectedAvailableCount === availableScopeCount;
  
  return (
    <Box
      sx={{
        minHeight: "100vh",
        backgroundColor: "#f8fafc",
        p: {
          xs: 2,
          sm: 3,
        },
      }}
    >
      <Paper
        elevation={0}
        sx={{
          mb: 3,
          borderRadius: 2,
          overflow: "hidden",
          border: "1px solid #e2e8f0",
          boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
        }}
      >
        <Box
          sx={{
            px: 3,
            py: 2.5,
            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            color: "#fff",
          }}
        >
          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              fontSize: {
                xs: "1rem",
                sm: "1.2rem",
              },
            }}
          >
            Add Scope Based on the Product
          </Typography>

          <Typography
            variant="body2"
            sx={{
              mt: 0.5,
              opacity: 0.9,
            }}
          >
            Select the material or product tested, then view disciplines and add
            available scopes.
          </Typography>
        </Box>
      </Paper>

      <Paper
        elevation={0}
        sx={{
          borderRadius: 2,
          border: "1px solid #e2e8f0",
          boxShadow: "0 2px 10px rgba(0,0,0,0.06)",
          p: {
            xs: 2,
            sm: 3,
          },
        }}
      >
        <Typography
          variant="h6"
          sx={{
            fontWeight: 600,
            color: "#334155",
            mb: 3,
            fontSize: "1.05rem",
          }}
        >
          Product Information
        </Typography>

        {materialError && (
          <Alert
            severity="error"
            sx={{
              mb: 3,
              borderRadius: 2,
            }}
          >
            {materialError}
          </Alert>
        )}

        <FormControl fullWidth required>
          <Autocomplete
            options={materials}
            getOptionLabel={(option) => option.GroupTested || ""}
            value={selectedMaterial}
            onChange={handleMaterialChange}
            loading={materialLoading}
            disabled={materialLoading}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Search or select Material or Product Tested"
                variant="outlined"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {materialLoading ? <CircularProgress size={20} /> : null}

                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
            noOptionsText={
              materialLoading ? "Loading..." : "No materials found"
            }
          />
          {selectedMaterial?.GroupTested && (
            <Box
              sx={{
                mt: 1,
                px: 1.5,
                py: 1,
                borderRadius: 1.5,
                backgroundColor: "#f8fafc",
                border: "1px solid #e2e8f0",
              }}
            >
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block" }}
              >
                Selected Material / Product Tested
              </Typography>

              <Typography
                variant="body2"
                sx={{
                  fontWeight: 600,
                  color: "#334155",
                  mt: 0.25,
                }}
              >
                {selectedMaterial.GroupTested}
              </Typography>
            </Box>
          )}
        </FormControl>

        {disciplineError && (
          <Alert
            severity="error"
            sx={{
              mt: 3,
              borderRadius: 2,
            }}
          >
            {disciplineError}
          </Alert>
        )}

        {disciplineLoading && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              mt: 3,
              p: 2,
              backgroundColor: "#f8fafc",
              borderRadius: 2,
            }}
          >
            <CircularProgress size={20} />

            <Typography variant="body2" color="text.secondary">
              Loading disciplines...
            </Typography>
          </Box>
        )}

        {!disciplineLoading &&
          selectedMaterial &&
          disciplines.length === 0 &&
          !disciplineError && (
            <Alert
              severity="info"
              sx={{
                mt: 3,
                borderRadius: 2,
              }}
            >
              No disciplines found for the selected material or product.
            </Alert>
          )}

        {!disciplineLoading && disciplines.length > 0 && (
          <Box sx={{ mt: 3 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 1.5,
              }}
            >
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 600,
                  color: "#334155",
                }}
              >
                Available Disciplines
              </Typography>
            </Box>

            <TableContainer
              sx={{
                border: "1px solid #e2e8f0",
                borderRadius: 2,
                overflow: "hidden",
              }}
            >
              <Table size="small">
                <TableHead>
                  <TableRow
                    sx={{
                      backgroundColor: "#f1f5f9",
                    }}
                  >
                    <TableCell>
                      <strong>Action</strong>
                    </TableCell>
                    <TableCell>
                      <strong>Discipline</strong>
                    </TableCell>
                    <TableCell>
                      <strong>Status</strong>
                    </TableCell>
                     <TableCell>
                      <strong>Parameters</strong>
                    </TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {disciplines.map((item) => {
                    const scopes = normalizeScopeDialogItems(item.ScopeMasterData);
                    const hasScopes = scopes.length > 0;
                    return (
                      <TableRow key={item.DisplineId} hover>
                        <TableCell>
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={() => openScopeDialog(item)}
                            disabled={!hasScopes}
                            sx={{
                              minWidth: "90px",
                              textTransform: "none",
                            }}
                          >
                            View Scope
                          </Button>
                        </TableCell>
                        <TableCell>
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 500,
                            }}
                          >
                            {item.DisciplineName || "-"}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={item.IsExists===1 ? "Discipline Available" : "Discipline Not Available"}
                            size="small"
                            variant="outlined"
                          />
                        </TableCell>
                         <TableCell>
                          <Chip
                            label={item.NoOfParameter || 0}
                            size="small"
                            variant="outlined"
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}
      </Paper>

      <Dialog
        open={scopeDialogOpen}
        onClose={closeScopeDialog}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            maxHeight: "88vh",
          },
        }}
      >
        <DialogTitle
          sx={{
            pb: 1.5,
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              gap: 2,
            }}
          >
            <Box>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                }}
              >
                {selectedScopeDialogDiscipline?.DisciplineName || "Scopes"}
              </Typography>

              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  mt: 0.5,
                }}
              >
                {selectedScopeDialogDiscipline?.GroupTested}
              </Typography>
            </Box>
            <Chip
              label={
                isDisciplineAdded ? "Discipline Added" : "Discipline Not Added"
              }
              size="small"
              color={isDisciplineAdded ? "success" : "warning"}
              variant="outlined"
            />
          </Box>
        </DialogTitle>
        <Divider />
        <DialogContent dividers>
          {!isDisciplineAdded && (
            <Alert
              severity="warning"
              sx={{
                mb: 2,
                borderRadius: 2,
                alignItems: "center",

                "& .MuiAlert-message": {
                  flex: 1,
                },

                "& .MuiAlert-action": {
                  alignItems: "center",
                  pt: 0,
                },
              }}
              action={
                <Button
                  variant="contained"
                  size="small"
                  onClick={() => {
                    navigate(
                      `/main/medical/discipline?appId=${encryptAppId(appId)}&orgId=${encryptAppId(orgId)}`,
                    );
                  }}
                  sx={{
                    textTransform: "none",
                    whiteSpace: "nowrap",
                  }}
                >
                  Add Discipline
                </Button>
              }
            >
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 600,
                }}
              >
                Discipline not added
              </Typography>

              <Typography
                variant="body2"
                sx={{
                  mt: 0.25,
                }}
              >
                Please add this discipline to the Discipline section first. Once
                the discipline has been added, you can then add the scope for
                it.
              </Typography>
            </Alert>
          )}

          {scopeDialogItems.length === 0 ? (
            <Alert
              severity="info"
              sx={{
                borderRadius: 2,
              }}
            >
              No scope master data available for this discipline.
            </Alert>
          ) : (
            <>
              <Box
                sx={{
                  p: 1.5,
                  mb: 2,
                  borderRadius: 2,
                  backgroundColor: "#f8fafc",
                  border: "1px solid #e2e8f0",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    flexWrap: "wrap",
                    gap: 1,
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 600,
                      color: "#334155",
                    }}
                  >
                    Scope Summary
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      gap: 1,
                      flexWrap: "wrap",
                    }}
                  >
                    <Chip
                      size="small"
                      label={`Total: ${totalScopeCount}`}
                      variant="outlined"
                    />
                    <Chip
                      size="small"
                      label={`Already Added: ${addedScopeCount}`}
                      color="success"
                      variant="outlined"
                    />
                    <Chip
                      size="small"
                      label={`Available: ${availableScopeCount}`}
                      color="primary"
                      variant="outlined"
                    />
                    {selectedAvailableCount > 0 && (
                      <Chip
                        size="small"
                        label={`Selected: ${selectedAvailableCount}`}
                        color="warning"
                      />
                    )}
                  </Box>
                </Box>
              </Box>
              <TableContainer
                sx={{
                  border: "1px solid #e2e8f0",
                  borderRadius: 2,
                  maxHeight: "48vh",
                }}
              >
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      {isDisciplineAdded && (
                        <TableCell
                          padding="checkbox"
                          sx={{
                            backgroundColor: "#f1f5f9",
                          }}
                        >
                          <Tooltip title="Select all available scopes">
                            <Checkbox
                              checked={allAvailableSelected}
                              indeterminate={
                                selectedAvailableCount > 0 &&
                                selectedAvailableCount < availableScopeCount
                              }
                              onChange={(e) =>
                                handleDialogSelectAll(e.target.checked)
                              }
                              disabled={availableScopeCount === 0}
                            />
                          </Tooltip>
                        </TableCell>
                      )}
                      <TableCell
                        sx={{
                          backgroundColor: "#f1f5f9",
                        }}
                      >
                        <strong>Test Code</strong>
                      </TableCell>
                      <TableCell
                        sx={{
                          backgroundColor: "#f1f5f9",
                        }}
                      >
                        <strong>Materials or Products Tested</strong>
                      </TableCell>
                      <TableCell
                        sx={{
                          backgroundColor: "#f1f5f9",
                        }}
                      >
                        <strong>Component / Parameter</strong>
                      </TableCell>
                      <TableCell
                        sx={{
                          backgroundColor: "#f1f5f9",
                        }}
                      >
                        <strong>Method Specification</strong>
                      </TableCell>
                      <TableCell
                        sx={{
                          backgroundColor: "#f1f5f9",
                        }}
                      >
                        <strong>Category</strong>
                      </TableCell>
                      <TableCell
                        sx={{
                          backgroundColor: "#f1f5f9",
                        }}
                      >
                        <strong>PT Availability</strong>
                      </TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {scopeDialogItems.map((scope, index) => {
                      const scopeKey = getScopeSelectionKey(scope, index);
                      const added = isScopeAdded(scope);
                      const available = isDisciplineAdded && !added;
                      const selected = selectedScopeDialogKeys.includes(scopeKey);

                      return (
                        <TableRow
                          key={scopeKey}
                          hover
                          sx={{
                            backgroundColor: added
                              ? "#f8faf8"
                              : selected
                                ? "#fff8e1"
                                : "inherit",
                          }}
                        >
                          {isDisciplineAdded && (
                            <TableCell padding="checkbox">
                              {available ? (
                                <Checkbox
                                  checked={selected}
                                  onChange={(e) =>
                                    handleDialogScopeToggle(
                                      scopeKey,
                                      e.target.checked,
                                    )
                                  }
                                />
                              ) : (
                                <Tooltip title="This scope has already been added">
                                  <Chip
                                    label="Already Added"
                                    size="small"
                                    color="success"
                                    variant="outlined"
                                  />
                                </Tooltip>
                              )}
                            </TableCell>
                          )}
                          <TableCell>
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: 600,
                              }}
                            >
                              {scope.TestCode || "-"}
                            </Typography>
                          </TableCell>
                          <TableCell>{scope.GroupTested || "-"}</TableCell>
                          <TableCell>{scope.TypeOfTest || "-"}</TableCell>
                          <TableCell>
                            <Typography
                              variant="body2"
                              sx={{
                                whiteSpace: "pre-line",
                              }}
                            >
                              {scope.ScopeStandard || "-"}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={scope.Category || "-"}
                              size="small"
                              color={
                                scope.Category === "Quantitative"
                                  ? "primary"
                                  : "secondary"
                              }
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={
                                scope.PTAvailable === "Yes" ||
                                scope.PTAvailable === "1"
                                  ? "Yes"
                                  : "No"
                              }
                              size="small"
                              color={
                                scope.PTAvailable === "Yes" ||
                                scope.PTAvailable === "1"
                                  ? "success"
                                  : "default"
                              }
                              variant="outlined"
                            />
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </DialogContent>

      <DialogActions
          sx={{
            px: 3,
            py: 2,
            gap: 1,
          }}
        >
          <Button
            onClick={closeScopeDialog}
            color="inherit"
            sx={{
              textTransform: "none",
            }}
          >
            Close
          </Button>

          {isDisciplineAdded && (
            <Tooltip
              title={
                availableScopeCount === 0
                  ? "All available scopes have already been added"
                  : selectedAvailableCount === 0
                    ? "Select at least one available scope"
                    : ""
              }
            >
              <span>
                <Button
                  variant="contained"
                  onClick={handleAddSelectedScopesFromDialog}
                  disabled={selectedAvailableCount === 0 || addingDisciplines}
                  startIcon={
                    addingDisciplines ? <CircularProgress size={18} /> : null
                  }
                  sx={{
                    textTransform: "none",
                    minWidth: 150,
                  }}
                >
                  {addingDisciplines
                    ? "Adding..."
                    : `Add Selected (${selectedAvailableCount})`}
                </Button>
              </span>
            </Tooltip>
          )}
        </DialogActions>
      </Dialog>
      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={closeNotification}
        anchorOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <Alert
          onClose={closeNotification}
          severity={notification.severity}
          sx={{
            width: "100%",
          }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AddScopeBasedOnProduct;